import type { TFunction } from 'i18next';
import type { PsychologicalResult } from '../../model';
import parseBurnoutInventory from './parseBurnoutInventory';
import parseGrowthMindset from './parseGrowthMindset';
import parseIndividualMotivation from './parseIndividualMotivation';
import parseMl360 from './parseMl360';
import parseOrganisationalAiReadiness from './parseOrganisationalAiReadiness';
import parseSelfDetermination from './parseSelfDetermination';
import parseTeamResilience from './parseTeamResilience';
import {
    buildBurnoutInventoryPlaceholder,
    buildGrowthMindsetPlaceholder,
    buildIndividualMotivationPlaceholder,
    buildOrganisationalAiReadinessPlaceholder,
    buildMl360Placeholder,
    buildSelfDeterminationPlaceholder,
    buildTeamResiliencePlaceholder,
} from './placeholders';
import type { AssessmentResultItemDataFragment } from '@/gql/graphql';

export const selfDeterminationHeight = 355;
export const growthMindsetHeight = 355;
export const burnoutInventoryHeight = 355;
export const organisationalAiReadinessHeight = 355;
export const teamResilienceHeight = 340;
export const ml360Height = 452;
export const individualMotivationHeight = 355;

const parsePsychologicalResults = (
    t: TFunction,
    items: AssessmentResultItemDataFragment[],
    teamResilienceItems: AssessmentResultItemDataFragment[],
    incompleteItems: AssessmentResultItemDataFragment[]
): PsychologicalResult[] => {
    const incompleteModuleInstanceIds = new Set(incompleteItems.map(i => i.moduleInstance?.id));

    const completedItems = items.filter(
        i => !i.moduleInstance?.id || !incompleteModuleInstanceIds.has(i.moduleInstance.id)
    );
    const completedTeamResilienceItems = teamResilienceItems.filter(
        i => !i.moduleInstance?.id || !incompleteModuleInstanceIds.has(i.moduleInstance.id)
    );

    const results: PsychologicalResult[] = [];

    // Self Determination
    const completedSelfDetermination = parseSelfDetermination(t, completedItems);
    if (completedSelfDetermination) {
        results.push(completedSelfDetermination);
    } else {
        const incompleteSelfDetermination = incompleteItems.find(
            i => i.result.__typename === 'SelfDeterminationResult'
        );
        if (incompleteSelfDetermination) {
            const p = buildSelfDeterminationPlaceholder(t, incompleteSelfDetermination);
            if (p) {
                results.push(p);
            }
        }
    }

    // Growth Mindset
    const completedGrowthMindset = parseGrowthMindset(t, completedItems);
    if (completedGrowthMindset) {
        results.push(completedGrowthMindset);
    } else {
        const growthMindsetFallback =
            completedItems.find(i => i.result.__typename === 'GrowthMindsetResult') ??
            incompleteItems.find(i => i.result.__typename === 'GrowthMindsetResult');
        if (growthMindsetFallback) {
            const p = buildGrowthMindsetPlaceholder(t, growthMindsetFallback);
            if (p) {
                results.push(p);
            }
        }
    }

    // Burnout Inventory
    const completedBurnoutInventory = parseBurnoutInventory(t, completedItems);
    if (completedBurnoutInventory) {
        results.push(completedBurnoutInventory);
    } else {
        const burnoutInventoryFallback =
            completedItems.find(i => i.result.__typename === 'BurnoutInventoryResult') ??
            incompleteItems.find(i => i.result.__typename === 'BurnoutInventoryResult');
        if (burnoutInventoryFallback) {
            const p = buildBurnoutInventoryPlaceholder(t, burnoutInventoryFallback);
            if (p) {
                results.push(p);
            }
        }
    }

    // Organisational AI Readiness
    const completedOAR = parseOrganisationalAiReadiness(t, completedItems);
    if (completedOAR) {
        results.push(completedOAR);
    } else {
        const oarFallback =
            completedItems.find(i => i.result.__typename === 'OrganisationalAiReadinessResult') ??
            incompleteItems.find(i => i.result.__typename === 'OrganisationalAiReadinessResult');
        if (oarFallback) {
            const p = buildOrganisationalAiReadinessPlaceholder(t, oarFallback);
            if (p) {
                results.push(p);
            }
        }
    }

    // Individual Motivation
    const completedIndividualMotivation = parseIndividualMotivation(t, completedItems);
    if (completedIndividualMotivation) {
        results.push(completedIndividualMotivation);
    } else {
        const incompleteIndividualMotivation = incompleteItems.find(
            i => i.result.__typename === 'IndividualMotivationResult'
        );
        if (incompleteIndividualMotivation) {
            const p = buildIndividualMotivationPlaceholder(t, incompleteIndividualMotivation);
            if (p) {
                results.push(p);
            }
        }
    }

    // Team Resilience
    const completedTeamResilience = parseTeamResilience(t, completedItems, completedTeamResilienceItems);
    if (completedTeamResilience) {
        results.push(completedTeamResilience);
    } else {
        const incompleteTeamResilience = incompleteItems.find(i => i.result.__typename === 'TeamResilienceResult');
        if (incompleteTeamResilience) {
            const p = buildTeamResiliencePlaceholder(t, incompleteTeamResilience);
            if (p) {
                results.push(p);
            }
        }
    }

    // ML360 (single aggregated section)
    const ml360CombinedItems = [...completedItems, ...incompleteItems].filter(
        i => i.result.__typename === 'ML360Result'
    );
    if (ml360CombinedItems.length > 0) {
        const completedMl360 = parseMl360(t, ml360CombinedItems);
        if (completedMl360) {
            results.push(completedMl360);
        } else {
            const p = buildMl360Placeholder(t, ml360CombinedItems);
            if (p) {
                results.push(p);
            }
        }
    }

    return results;
};

export default parsePsychologicalResults;
